import {
  LANGFUSE_METADATA_HEADER,
  buildLangfuseMetadata,
  createLangfuseRunId,
  encodeLangfuseMetadataHeader,
  type LangfusePostedObservation,
  postLangfuseObservation,
  postLangfuseOutcome,
  startLangfuseRun,
  withLangfuseMetadataHeader,
} from "../shared/services/langfuseTrace";
import type { WorkbookContent } from "./types";

export { LANGFUSE_METADATA_HEADER, encodeLangfuseMetadataHeader };
export const EXCEL_LANGFUSE_TRACE_NAME = "FactSetAddIn.Excel.ai.run";

export type ExcelLangfusePhase =
  | "planner"
  | "executor"
  | "multi_hop_executor"
  | "excel_unified_agent"
  | "verifier"
  | "classifier"
  | "answer_question"
  | "simple_query"
  | "embedding"
  | "outcome";

export interface ExcelLangfuseRunContext {
  runId: string;
  requestId: string;
  correlationId: string;
  sessionId?: string;
  host: "excel";
  surface: "task_pane";
  mode: string;
  hasContext: boolean;
  workbook?: {
    sheetCount: number;
    activeSheetName?: string;
    displayedRowCount?: number;
    displayedCellCount?: number;
  };
  traceId?: string;
  rootSpanId?: string;
  traceInput?: unknown;
  traceStartedAt?: string;
}

export interface ExcelLangfuseOutcome {
  status: "completed" | "failed" | "cancelled";
  durationMs: number;
  errorType?: string;
  needsClarification?: boolean;
  workbookEditAttempted?: boolean;
  workbookEditSuccess?: boolean;
  operationCount?: number;
  affectedSheetCount?: number;
}

export interface ExcelLangfuseObservation extends LangfusePostedObservation {}

/** Executor architecture recorded without changing the unified-agent schema. */
export type ExcelUnifiedAgentExecutorMode =
  | "plan-protocol"
  | "native-react";

/** Inputs shared by every unified-agent provider generation. */
export interface ExcelUnifiedAgentGenerationMetadataInput {
  /** Executor architecture selected once at run admission. */
  executorMode: ExcelUnifiedAgentExecutorMode;
  /** Provider family serving the generation. */
  provider: string;
  /** Provider-specific model identifier used for token pricing. */
  model: string;
  /** One-based executor iteration number. */
  iteration: number;
  /** Whether the provider response uses streaming transport. */
  stream: boolean;
  /** Number of tools exposed on this model turn. */
  toolCount: number;
}

/**
 * Build the canonical Langfuse metadata for an Excel unified-agent generation.
 *
 * Both orchestration implementations consume this builder so phase, label, and
 * token-linkage fields cannot drift. `executorMode` is the sole intentional
 * discriminator and supports side-by-side analysis within one trace schema.
 *
 * @param input - Provider, model, iteration, tool count, and executor mode.
 * @returns Canonical metadata attached to the provider request.
 */
export function buildExcelUnifiedAgentGenerationMetadata(
  input: ExcelUnifiedAgentGenerationMetadataInput,
): Record<string, unknown> {
  return {
    phase: "excel_unified_agent",
    label: "excel-unified-agent",
    executorMode: input.executorMode,
    provider: input.provider,
    model: input.model,
    iteration: input.iteration,
    stream: input.stream,
    toolCount: input.toolCount,
  };
}

export function createExcelLangfuseRunId(): string {
  return createLangfuseRunId("excel");
}

export function summarizeWorkbookForLangfuse(
  workbookContent: WorkbookContent | null | undefined,
): ExcelLangfuseRunContext["workbook"] | undefined {
  if (!workbookContent) return undefined;

  let displayedRowCount = 0;
  let displayedCellCount = 0;
  let hasDisplayedGridMetrics = false;
  for (const sheet of workbookContent.sheets || []) {
    const usedRange = sheet.usedRange;
    if (!usedRange) continue;
    const values = usedRange.values;
    if (Array.isArray(values)) {
      hasDisplayedGridMetrics = true;
      displayedRowCount += values.length;
      displayedCellCount += values.reduce(
        (sum, row) => sum + (Array.isArray(row) ? row.length : 0),
        0,
      );
      continue;
    }
    if (
      typeof usedRange.rowCount === "number" &&
      usedRange.rowCount >= 0 &&
      typeof usedRange.columnCount === "number" &&
      usedRange.columnCount >= 0
    ) {
      hasDisplayedGridMetrics = true;
      displayedRowCount += usedRange.rowCount;
      displayedCellCount += usedRange.rowCount * usedRange.columnCount;
    }
  }

  return {
    sheetCount: workbookContent.sheets?.length ?? 0,
    activeSheetName: workbookContent.activeSheetName,
    ...(hasDisplayedGridMetrics
      ? {
          displayedRowCount,
          displayedCellCount,
        }
      : {}),
  };
}

export function createExcelLangfuseRunContext(params: {
  mode: string;
  hasContext: boolean;
  sessionId?: string;
  workbookContent?: WorkbookContent | null;
}): ExcelLangfuseRunContext {
  const runId = createExcelLangfuseRunId();
  return {
    runId,
    requestId: runId,
    correlationId: runId,
    // Never substitute runId: Langfuse sessionId is the persisted chat-tab ID.
    sessionId: params.sessionId,
    host: "excel",
    surface: "task_pane",
    mode: params.mode,
    hasContext: params.hasContext,
    workbook: summarizeWorkbookForLangfuse(params.workbookContent),
  };
}

export async function startExcelLangfuseRun(
  context: ExcelLangfuseRunContext,
  input: unknown,
): Promise<void> {
  await startLangfuseRun({
    endpoint: "/api/ai/langfuse/excel-run-start",
    context,
    metadata: buildExcelLangfuseMetadata(context, { phase: "root" }),
    input,
    hostLabel: "Excel",
  });
}

export function buildExcelLangfuseMetadata(
  context: ExcelLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  return buildLangfuseMetadata(
    context || { host: "excel", surface: "task_pane" },
    EXCEL_LANGFUSE_TRACE_NAME,
    overrides,
  );
}

export function withExcelLangfuseMetadata(
  init: RequestInit,
  context: ExcelLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): RequestInit {
  return withLangfuseMetadataHeader(init, buildExcelLangfuseMetadata(context, overrides));
}

/**
 * Attaches the Langfuse metadata header only when a run context is present, so
 * out-of-scope Excel provider calls (no instrumented run) stay untraced
 * server-side instead of creating ungrouped traces.
 */
export function withOptionalExcelLangfuseMetadata(
  init: RequestInit,
  context: ExcelLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): RequestInit {
  if (!context) return init;
  return withExcelLangfuseMetadata(init, context, overrides);
}

export function postExcelLangfuseOutcome(
  context: ExcelLangfuseRunContext | undefined,
  outcome: ExcelLangfuseOutcome,
): void {
  if (!context) return;

  postLangfuseOutcome({
    endpoint: "/api/ai/langfuse/excel-run-outcome",
    metadata: buildExcelLangfuseMetadata(context, { phase: "outcome" }),
    outcome,
    input: context.traceInput,
    startTime: context.traceStartedAt,
    hostLabel: "Excel",
  });
}

/**
 * Fire-and-forget POST of a single tool-call observation, grouped under the
 * run's phase span server-side. No-ops without a run context or an unnamed
 * observation so out-of-scope calls stay untraced. Mirrors the PowerPoint
 * `postPptxLangfuseObservation` so the two hosts capture tool I/O identically.
 */
export function postExcelLangfuseObservation(
  context: ExcelLangfuseRunContext | undefined,
  observation: ExcelLangfuseObservation,
  options?: { phase?: ExcelLangfusePhase },
): void {
  if (!context || !observation?.name?.trim()) return;

  postLangfuseObservation({
    endpoint: "/api/ai/langfuse/excel-observation",
    metadata: buildExcelLangfuseMetadata(context, { phase: options?.phase ?? "multi_hop_executor" }),
    observation,
    hostLabel: "Excel",
  });
}
